import { ColorItem } from "components/ColorItem/ColorItem"
import { type UICustomizationData } from "pages/UICustomization/types.ts"
import { type ChangeEvent } from "react"
import { useTranslation } from "react-i18next"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { defaultStyles } from "./defaultStyles.ts"
import { getStylesTemplate } from "./stylesTemplate"

interface UICustomizationStylesProps {
  data: UICustomizationData
  onChange: (data: UICustomizationData) => void
}

const ButtonPreview = ({ stylesData }: { stylesData: Record<string, string> }) => {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-white p-4">
      {/* Primary */}
      <button
        style={{
          padding: "8px 20px",
          fontSize: "13px",
          borderRadius: stylesData["tint-button-base-radius"],
          background: stylesData["tint-button-primary-background"],
          color: stylesData["tint-button-primary-text"],
          border: "none",
        }}
      >
        {t("uiCustomizationStyles.buttonType.primary")}
      </button>
      <button
        style={{
          padding: "8px 20px",
          fontSize: "13px",
          borderRadius: stylesData["tint-button-base-radius"],
          background: stylesData["tint-button-primary-background-hover"],
          color: stylesData["tint-button-primary-text"],
          border: "none",
          opacity: 0.8,
        }}
      >
        {t("uiCustomizationStyles.buttonType.primary")} hover
      </button>
      {/* Secondary */}
      <button
        style={{
          padding: "8px 20px",
          fontSize: "13px",
          borderRadius: stylesData["tint-button-base-radius"],
          background: stylesData["tint-button-secondary-background"],
          color: stylesData["tint-text-dark"],
          border: "none",
        }}
      >
        {t("uiCustomizationStyles.buttonType.secondary")}
      </button>
      <button
        style={{
          padding: "8px 20px",
          fontSize: "13px",
          borderRadius: stylesData["tint-button-sm-radius"],
          background: stylesData["tint-button-secondary-background-disabled"],
          color: stylesData["tint-text-dark"],
          border: "none",
          opacity: 0.5,
        }}
      >
        disabled
      </button>
    </div>
  )
}

export const UICustomizationStyles = ({ data, onChange }: UICustomizationStylesProps) => {
  const { t } = useTranslation()
  const stylesTemplate = getStylesTemplate(t)
  const stylesData: Record<string, string> = { ...defaultStyles, ...data.styles }

  const updateStyle = (key: string, value: string) => {
    onChange({ ...data, styles: { ...stylesData, [key]: value } })
  }

  return (
    <div className="space-y-3">
      {stylesTemplate.map(({ title, description, styles }) => (
        <Card key={title}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {description && <CardDescription className="text-xs">{description}</CardDescription>}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {styles.map((item, index) => {
                if (item.type === "separator") return <Separator key={`sep-${index}`} />

                if (item.type === "buttonExample")
                  return <ButtonPreview key="btn-preview" stylesData={stylesData} />

                return (
                  <div key={item.key} className="flex items-center gap-3">
                    {item.type === "color" && (
                      <ColorItem
                        value={stylesData[item.key!]}
                        onChange={(value) => updateStyle(item.key!, value)}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <Label className="text-sm">{item.title}</Label>
                      <p className="truncate text-xs text-muted-foreground">{item.key}</p>
                    </div>
                    {item.type === "size" && (
                      <Input
                        className={`w-28 text-right ${
                          !stylesData[item.key!] ? "border-destructive" : ""
                        }`}
                        value={stylesData[item.key!]}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          updateStyle(item.key!, e.target.value)
                        }
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
